import { DateTimeFactory } from "../../../../../modules/localization/useDateTime"
import { TranslationKey } from "../../../../../modules/localization/translations"
import { MeetingServiceOrder } from "../../../../../modules/merchant/accounting/accounting/accountingTypes"
import { CreateUpdateBookingResourceTableSetups } from "../../../dialogs/createUpdateBookingDialog/createUpdateBookingTypes"
import {
    CreateOrderBookingPrefill,
    CreateOrderDialogFlow,
    CreateOrderDialogMode,
    ResourceFiltersSelectedItem,
    ResourceItem,
} from "../../createOrderTypes"
import { EventsForServiceCenterResponse } from "../../createOrderApi"
import { NullableCalendarEvent, TimelineCalendarEvent, TimelineCalendarResource } from "./calendar/fullCalendarTypes"
import { createInitEvents, getEventBackgroundColor } from "./resourceBookingCalendars/helpers"

/** `availabilityView` is one character per interval: "0" = free, anything else = busy. */
export const isAvailabilityViewFree = (availabilityView: string): boolean =>
    [...availabilityView].every((c) => c === "0")

export function toTimelineEvents(
    response: EventsForServiceCenterResponse,
    dateTimeFactory: DateTimeFactory,
): TimelineCalendarEvent[] {
    return (response.events ?? []).map((event) => ({
        id: String(event.orderId),
        resourceIds: event.resourceIds.map(String),
        start: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(event.startDateTime)),
        end: dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(event.endDateTime)),
        subject: event?.subject || "",
        host: event?.host || "",
        canEdit: !!event?.canEdit,
        outOfService: !!event?.outOfService,
        backgroundColor: getEventBackgroundColor({
            outOfService: !!event?.outOfService,
            canEdit: !!event?.canEdit,
        }),
    }))
}

export function hasEventOverlap(
    args: Readonly<{
        start: string
        end: string
        buffer: number
        events: ReadonlyArray<TimelineCalendarEvent>
        dateTimeFactory: DateTimeFactory
        excludeEventId?: string
    }>,
): boolean {
    const { start, end, buffer, events, dateTimeFactory, excludeEventId } = args

    const bufferedStart = dateTimeFactory.fromISO(start).minus({ minutes: buffer })
    const bufferedEnd = dateTimeFactory.fromISO(end).plus({ minutes: buffer })

    return events.some((event) => {
        if (excludeEventId && event.id === excludeEventId) return false

        const eventStart = dateTimeFactory.fromISO(event.start)
        const eventEnd = dateTimeFactory.fromISO(event.end)

        return (
            (bufferedStart < eventEnd && bufferedStart >= eventStart) ||
            (bufferedEnd > eventStart && bufferedEnd <= eventEnd) ||
            (bufferedStart <= eventStart && bufferedEnd >= eventEnd)
        )
    })
}

export const filterResources = (
    resources: TimelineCalendarResource[],
    filters: ResourceFiltersSelectedItem[],
    searchRegex: RegExp | undefined,
    activeResourceIds: readonly string[] | null,
    busyResourceIds?: ReadonlySet<string>,
): TimelineCalendarResource[] => {
    if (!filters.length && !searchRegex && !busyResourceIds?.size) return resources

    return resources
        .map((resource) => {
            const isPartOfActiveBooking = !!activeResourceIds?.includes(resource.id)

            const passesFilters = filters.every((filter) => {
                const filterValues = filter.values.map((v) => v.value)

                if (!filterValues.length) return true

                switch (filter.identifier) {
                    case "resourceTypeIds":
                        return resource.type ? filterValues.some((fv) => Number(fv) === resource.type?.id) : false
                    case "floors":
                        return resource.floor !== undefined && resource.floor !== null
                            ? filterValues.some((fv) => Number(fv) === resource.floor)
                            : false
                    case "fixedAvIds":
                        return filterValues.every((filterValue) =>
                            resource.avEquipments?.some((av) => av.id === filterValue),
                        )
                    case "sittings":
                        // falsy seats (0 / null / undefined) means unknown capacity, not "does not fit"
                        return resource.seats ? resource.seats >= Number(filterValues[0]) : true
                    case "serviceCenterIds": {
                        if (!resource.locationId) return false
                        const serviceCenterId = Number(resource.locationId.split("_")[0])
                        return filterValues.some((fv) => Number(fv) === serviceCenterId)
                    }
                    case "tableSetupIds": {
                        if (!resource.tableSetups || resource.tableSetups.length === 0) return false
                        return resource.tableSetups.some((ts) => filterValues.includes(ts.key))
                    }
                    default:
                        return true
                }
            })

            const passesSearch = searchRegex ? searchRegex.test(resource.title) : true
            const passesAvailability = !busyResourceIds?.has(resource.id)

            const isDimmed = (!passesFilters || !passesSearch || !passesAvailability) && isPartOfActiveBooking

            if (!((passesFilters && passesSearch && passesAvailability) || isPartOfActiveBooking)) return null

            // Avoid recreating resource objects unless we actually need to add `isDimmed`.
            return isDimmed ? { ...resource, isDimmed: true } : resource
        })
        .filter((resource) => resource !== null) as TimelineCalendarResource[]
}

type Group = {
    typeId: number
    typeName: TranslationKey
    resources: ResourceItem[]
}

const typeLocalizationMap: Record<number, TranslationKey> = {
    1: "create-order-confirm-booking-rooms",
    2: "create-order-confirm-booking-equipment",
    3: "create-order-confirm-booking-cars",
}

export function groupResourcesForRendering(resources: ResourceItem[]): Group[] {
    const groupMap = new Map<number, Group>()

    for (const resource of resources) {
        const { id: typeId } = resource.type

        if (!groupMap.has(typeId)) {
            groupMap.set(typeId, {
                typeId,
                typeName: typeLocalizationMap[typeId],
                resources: [],
            })
        }

        groupMap.get(typeId)!.resources.push(resource)
    }

    return Array.from(groupMap.values())
}

export function buildFinalResources(
    resourceIds: string[],
    resources: CreateUpdateBookingResourceTableSetups[],
): CreateUpdateBookingResourceTableSetups[] {
    const idSet = new Set(resourceIds.map(Number))
    const validResources = resources.filter((r) => idSet.has(r.id))

    const missingResources = Array.from(idSet)
        .filter((id) => !resources.some((r) => r.id === id))
        .map((id) => ({ id, tableSetups: [] }))

    return [...validResources, ...missingResources]
}

export type BookingSeed = Readonly<{
    event: NullableCalendarEvent
    fullResourcesList: ResourceItem[]
}>

/**
 * The booking an order starts out with, derived purely from the server order.
 *
 * One source for the two seeds that have to agree: the outer form's `event`/`fullResourcesList`
 * (through `getCustomFieldsValues`) and the timeline draft (through `ResourceBookingDraftProvider`).
 * The draft seeds from here rather than reading Formik back, because the provider is a child of
 * `<Formik>` and would observe the previous order's values on the render where the order changes.
 */
export function buildBookingSeedFromOrder({
    orderDetails,
    dateTimeFactory,
    mode,
    flowConfig,
    bookingPrefill,
}: Readonly<{
    orderDetails?: MeetingServiceOrder
    dateTimeFactory: DateTimeFactory
    mode?: CreateOrderDialogMode
    flowConfig?: CreateOrderDialogFlow
    bookingPrefill?: CreateOrderBookingPrefill
}>): BookingSeed {
    if (!orderDetails) {
        // Only the resource id is known here; the timeline resolves the rest once its resources load.
        return mode === CreateOrderDialogMode.CREATE &&
            bookingPrefill &&
            dateTimeFactory.fromISO(bookingPrefill.start) < dateTimeFactory.fromISO(bookingPrefill.end)
            ? {
                  event: createInitEvents(
                      [String(bookingPrefill.resourceId)],
                      bookingPrefill.start,
                      bookingPrefill.end,
                  ),
                  fullResourcesList: [],
              }
            : { event: null, fullResourcesList: [] }
    }

    // `meetingDetails` is typed as required but can be absent at runtime: `patchedOrderDetails` casts
    // an Outlook session order to `MeetingServiceOrder` before it necessarily has any.
    const { bookingDetails, meetingDetails } = orderDetails
    const start = meetingDetails?.startDateTime
        ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.startDateTime))
        : ""
    const end = meetingDetails?.endDateTime
        ? dateTimeFactory.toApiISOMinutesFormat(dateTimeFactory.fromApi(meetingDetails.endDateTime))
        : ""
    const bookedResources = bookingDetails?.resources ?? []

    if (mode === CreateOrderDialogMode.EDIT && bookedResources.length > 0) {
        const resourceIds = bookedResources.map((resource) => resource.id.toString())

        return {
            event: start && end ? createInitEvents(resourceIds, start, end) : null,
            fullResourcesList: bookedResources.map((resource) => ({
                title: resource.name,
                id: resource.id,
                type: {
                    id: resource.resourceTypeId,
                    name: resource.resourceType,
                },
                ...(resource.room?.tableSetup
                    ? {
                          tableSetups: [
                              {
                                  key: resource.room.tableSetup.key,
                                  name: resource.room.tableSetup.name,
                                  imageUrl: resource.room.tableSetup.imageUrl,
                              },
                          ],
                      }
                    : {}),
            })),
        }
    }

    // An Outlook session carries the meeting time before any resource is picked, and the timeline
    // works with that event. A resourceless seed is a valid state here, not a degenerate one.
    if (flowConfig && start && end) {
        return { event: createInitEvents([], start, end), fullResourcesList: [] }
    }

    return { event: null, fullResourcesList: [] }
}

export function getMeetingLocationFromBookedResources(
    args: Readonly<{
        bookedResources: ReadonlyArray<CreateUpdateBookingResourceTableSetups> | null | undefined
        fullResourcesList: ReadonlyArray<ResourceItem> | null | undefined
    }>,
): string {
    const ids = (args.bookedResources ?? []).map((r) => r.id)
    if (ids.length === 0) return ""

    const byId = new Map((args.fullResourcesList ?? []).map((r) => [r.id, r.title]))
    return ids
        .map((id) => byId.get(id)?.trim() ?? "")
        .filter(Boolean)
        .join(", ")
}
